import { ApproveStatus } from '@shared/enums/dpr';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
} from '@angular/core';
import { ProjectSetup, SelectedSubproject } from '@shared/models';
import { ReportConfigurationTerminology } from '@shared/models/project-setup';
import { DprDetails } from '@shared/models/dpr';
import { toUTCDate } from '@shared/utils';
import { DprApprovalStatusSummaryReport } from '@report/models';
import { UtcDateTimeOptions } from '@shared/enums';

@Component({
  selector: 'app-report-header',
  templateUrl: './report-header.component.html',
  styleUrls: ['./report-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportHeaderComponent implements OnInit {
  @Input({ required: true }) selectedSubproject: SelectedSubproject;
  @Input({ required: true }) dprDetails: DprDetails;

  approvalStatusSummary: DprApprovalStatusSummaryReport[] = [];
  shouldDisplayApprovalStatusSummary: boolean;

  get projectSetup(): ProjectSetup {
    return this.selectedSubproject.data;
  }

  private get terminology(): ReportConfigurationTerminology {
    return this.projectSetup.reportConfiguration?.reportHeadersTerminology;
  }

  private get defaultTerminology(): ReportConfigurationTerminology {
    return this.projectSetup.reportConfiguration?.defaultTerminologyValues;
  }

  get dprDate(): Date {
    return toUTCDate(
      new Date(this.dprDetails.dprDateTime),
      UtcDateTimeOptions.UtcDateWithoutTime
    );
  }

  get reportLabel(): string {
    return this.resolveTerminology('reportLabel');
  }

  get subprojectName(): string {
    return this.resolveTerminology('subprojectName');
  }

  get projectLabel(): string {
    return this.resolveTerminology('projectLabel');
  }

  get projectNameValue(): string {
    return this.resolveTerminology('projectName');
  }

  get clientLabel(): string {
    return this.resolveTerminology('clientLabel');
  }

  get clientNameValue(): string {
    return this.resolveTerminology('clientName');
  }

  get onlineApproversLabel(): string {
    return this.resolveTerminology('onlineApproversLabel');
  }

  get onlineApproversStatus(): string {
    return this.resolveTerminology('onlineApproversStatus');
  }

  private resolveTerminology(
    key: keyof Omit<ReportConfigurationTerminology, 'id' | 'subprojectId'>
  ): string {
    return this.terminology?.[key] || this.defaultTerminology?.[key];
  }

  get isRowEmpty(): boolean {
    return (
      !this.shouldDisplayApprovalStatusSummary &&
      String.isNullOrWhiteSpace(this.projectLabel) &&
      String.isNullOrWhiteSpace(this.projectNameValue) &&
      String.isNullOrWhiteSpace(this.clientLabel) &&
      String.isNullOrWhiteSpace(this.clientNameValue) &&
      String.isNullOrWhiteSpace(this.onlineApproversLabel) &&
      String.isNullOrWhiteSpace(this.onlineApproversStatus)
    );
  }

  get isTableHeadEmpty(): boolean {
    return (
      String.isNullOrWhiteSpace(
        this.projectSetup.subprojectConfiguration?.base64EncodedLogoContent
      ) &&
      String.isNullOrWhiteSpace(this.reportLabel) &&
      String.isNullOrWhiteSpace(this.subprojectName)
    );
  }

  ngOnInit(): void {
    this.approvalStatusSummary.push(
      {
        approveStatus: ApproveStatus.Accepted,
        approveStatusCount: (this.dprDetails.dprOnlineApprovals ?? []).filter(
          (item) => item.approveStatus === ApproveStatus.Accepted
        ).length,
      },
      {
        approveStatus: ApproveStatus.Rejected,
        approveStatusCount: (this.dprDetails.dprOnlineApprovals ?? []).filter(
          (item) => item.approveStatus === ApproveStatus.Rejected
        ).length,
      },
      {
        approveStatus: ApproveStatus.Seen,
        approveStatusCount: (this.dprDetails.dprOnlineApprovals ?? []).filter(
          (item) => item.approveStatus === ApproveStatus.Seen
        ).length,
      },
      {
        approveStatus: ApproveStatus.NotApproved,
        approveStatusCount: this.projectSetup.onlineApproverList.filter(
          (approver) => {
            if (approver.canApprove) {
              return (
                (this.dprDetails.dprOnlineApprovals ?? [])
                  .map((x) => x.accountId)
                  .findIndex(
                    (accountId) => accountId === approver.accountId
                  ) === -1
              );
            }
            return 0;
          }
        ).length,
      }
    );

    this.shouldDisplayApprovalStatusSummary = this.approvalStatusSummary.some(
      (x) => x.approveStatusCount > 0
    );
  }
}
